// Client-only tunables: timing and limits that shape how the SPA behaves in
// the browser and that no other layer shares. Anything the Worker or the
// schemas also need lives in shared/constants.ts.

/** How long a toast stays before it dismisses itself. */
export const TOAST_DURATION_MS = 4000;

/**
 * How long a read stays in memory after nothing is using it. A day, so a
 * technician walking a site with no signal moves between rooms without
 * every page having to go back to the offline copy.
 */
export const QUERY_CACHE_TIME_MS = 24 * 60 * 60 * 1000;

/** How many times a read that failed to reach the server is retried. */
export const QUERY_RETRY_LIMIT = 2;
/** The wait before the first retry; each later retry doubles it. */
export const QUERY_RETRY_BASE_DELAY_MS = 1000;
/** The longest wait between retries, however many have happened. */
export const QUERY_RETRY_MAX_DELAY_MS = 4000;

/** The history state a sheet pushes so the back button closes it. */
export const SHEET_HISTORY_STATE = { sheet: true } as const;

/** The key the command palette answers to (with Ctrl or Cmd). */
export const SEARCH_SHORTCUT_KEY = 'k';
/** The key that toggles the tree sidebar (with Ctrl or Cmd). */
export const SIDEBAR_SHORTCUT_KEY = 'b';
/** The key that opens the keyboard-shortcut overlay, outside a field. */
export const SHORTCUTS_KEY = '?';
/** The key that also focuses search, when focus is not in a field. */
/** Local-storage key for whether the tree side panel is hidden on this device. */
export const SIDEBAR_STORAGE_KEY = 'project-map.sidebar';
export const SEARCH_SLASH_KEY = '/';

// --- Editor pages (ROADMAP P1) ---------------------------------------------

/**
 * Where the editor pages stop being one column and become a board: palette
 * left, canvas centre, inspector right, panel along the bottom. The same
 * width the rack inspector has used since A10, named once so the component
 * and its stylesheet cannot drift apart.
 */
export const DESKTOP_WIDTH_QUERY = '(width >= 64rem)';
/** The key that hides and shows an editor's side panels (with Ctrl or Cmd). */
export const EDITOR_PANELS_SHORTCUT_KEY = 'h';
/** Local-storage key for whether the editor side panels are hidden on this device. */
export const EDITOR_PANELS_STORAGE_KEY = 'project-map.editor-panels';

// --- Security page ---------------------------------------------------------

/** Digits an authenticator app shows (TOTP_DIGITS in worker/auth/totp-constants.ts). */
export const MFA_APP_CODE_LENGTH = 6;
/** How long a copy button says "Copied" before it offers to copy again. */
export const COPIED_FOR_MS = 1500;
/** "Last active" reads as a relative time up to this age, then as a date. */
export const RELATIVE_TIME_MAX_DAYS = 7;
export const SECONDS_PER_MINUTE = 60;
export const MINUTES_PER_HOUR = 60;
export const HOURS_PER_DAY = 24;

// --- API page -------------------------------------------------------------

/** The expiries the token form offers, in days; "never" is the other choice. */
export const TOKEN_EXPIRY_DAY_CHOICES = [30, 90, 365] as const;
/** The choice the form starts on. */
export const TOKEN_EXPIRY_DEFAULT_DAYS = 90;
/** The value of the form's "never expires" choice. */
export const TOKEN_EXPIRY_NEVER = 'never';

// --- Needs attention (src/pages/AttentionPage.tsx) --------------------------

/**
 * How many rows of one "Needs attention" list are drawn before the rest wait
 * behind a "Show N more" button. Twenty gaps, each holding up to the Worker's
 * `ATTENTION_ROW_LIMIT`, laid out hundreds of rows nobody had scrolled to.
 */
export const ATTENTION_PAGE_ROWS = 50;

// --- Client and property lists ---------------------------------------------

/**
 * How many rows or cards one page of a location list draws before the rest
 * wait behind "Show N more": the dashboard's clients table, the same list as
 * cards, and a client's properties as cards. Fifty is what the attention
 * lists page by as well, but it is named apart from `ATTENTION_PAGE_ROWS`
 * because the two answer to different pressures — twenty short gap lists on
 * one page there, one list of every client the estate holds here — and
 * either can be tuned without moving the other.
 */
export const LOCATION_PAGE_ROWS = 50;

/**
 * How many clients the rail lists before the rest wait behind "Show N more".
 * Smaller than a page of the table: the rail is one narrow column beside the
 * page, and twenty-five 2.5rem rows are about a screen of it. The filter box
 * above the list, not the button, is how a technician with 1,400 clients
 * reaches one that sorts past here.
 */
export const SIDEBAR_CLIENT_PAGE_ROWS = 25;

// --- Notes log -------------------------------------------------------------

/** The note form shows how much room is left once within this many characters of the limit. */
export const NOTE_COUNTER_FROM_REMAINING = 500;

// --- Runs from a pattern ---------------------------------------------------

/** How many of the labels a batch would make the form reads back, before "… last". */
export const DROP_BATCH_PREVIEW_LABELS = 3;

// --- Label sheets (src/pages/LabelsPage.tsx) --------------------------------

/**
 * A Letter sheet of Avery 5160 labels (the geometry every 30-up address
 * sheet shares): three columns of ten, each 2.625 × 1 in, with 0.5 in above
 * and below, 0.1875 in either side, 0.125 in between columns, and no gap
 * between rows. The page prints with no printer margin so the grid lands
 * in register with the die cuts.
 */
export const LABEL_SHEET = {
  columns: 3,
  rows: 10,
  labelWidthIn: 2.625,
  labelHeightIn: 1,
  marginTopIn: 0.5,
  marginLeftIn: 0.1875,
  columnGapIn: 0.125,
  pageWidthIn: 8.5,
  pageHeightIn: 11,
} as const;
/** Labels per sheet: the grid, so a page break falls where the sheet ends. */
export const LABELS_PER_SHEET = LABEL_SHEET.columns * LABEL_SHEET.rows;
/** A Brother or DYMO tape label: 24 mm tall, as wide as its words. */
export const LABEL_TAPE_HEIGHT_MM = 24;
// --- Photo annotation (src/photos/annotate.ts) -----------------------------

// Marks are sized as a fraction of the image's longest edge, so the same
// shape list reads the same on the 2048 px image and on its 320 px
// thumbnail. Every mark is a dark halo with a bright stroke over it, which
// is what makes an arrow visible on a white wall plate and on a black rack.

/** The bright pass: amber reads against both ends of the range a site photo covers. */
export const ANNOTATION_COLOUR = '#ffc400';
/** The halo under it. */
export const ANNOTATION_HALO_COLOUR = '#101010';
/** Stroke width as a fraction of the longest edge (about 8 px on a 2048 px photo). */
export const ANNOTATION_STROKE_RATIO = 0.004;
/** The halo is wider than the stroke, so it shows on both sides of it. */
export const ANNOTATION_HALO_RATIO = 0.009;
/** An arrow head's barbs, as a fraction of the longest edge. */
export const ANNOTATION_ARROW_HEAD_RATIO = 0.045;
/** How far each barb turns off the shaft, in radians (30°). */
export const ANNOTATION_ARROW_HEAD_ANGLE = Math.PI / 6;
/** The one text size: a fraction of the longest edge (about 74 px on a 2048 px photo). */
export const ANNOTATION_FONT_RATIO = 0.036;
/** Text is drawn in the app's own stack, so no font has to be fetched to burn it in. */
export const ANNOTATION_FONT_STACK =
  'system-ui, -apple-system, "Segoe UI", Roboto, Helvetica, Arial, sans-serif';
/** A mark drawn with less than this much movement is a tap, not a shape, and is discarded. */
export const ANNOTATION_MIN_DRAG = 0.01;

// --- Barcode scanning (src/scan/) ------------------------------------------

/** Milliseconds between decode attempts on the camera preview: fast enough to feel live. */
export const SCAN_FRAME_INTERVAL_MS = 250;
